import React from 'react';
import { figure5, figure6, table1, table2, table3, table4 } from '../assets';

function Figure({ src, alt, caption, widthClass = 'max-w-2xl' }) {
  return (
    <figure className="my-10 mx-auto card p-4 md:p-6 w-full">
      <div className={`mx-auto ${widthClass}`}>
        <img src={src} alt={alt} className="w-full h-auto rounded-lg" />
      </div>
      <figcaption className="figure-caption">{caption}</figcaption>
    </figure>
  );
}

const units = [
  {
    abbr: 'ESU',
    name: 'Exclusive Safety Units',
    desc: 'Linked exclusively (relatively) to the safety attribute.',
    color: 'bg-grad-unit-brand',
  },
  {
    abbr: 'EUU',
    name: 'Exclusive Utility Units',
    desc: 'Linked exclusively (relatively) to the utility attribute.',
    color: 'bg-grad-unit-emerald',
  },
  {
    abbr: 'CU',
    name: 'Complex Units',
    desc: 'Contribute to both safety and utility attributes.',
    color: 'bg-grad-unit-accent',
  },
  {
    abbr: 'RU',
    name: 'Redundant Units',
    desc: 'Not associated with any attribute — repurposable.',
    color: 'bg-grad-unit-ink',
  },
];

function LessIsMoreSection() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">05 &middot; Empirical Study</p>
        <h2 className="section-title">Less is More for Safety Alignment</h2>

        <div className="prose-paper max-w-3xl mx-auto mt-10 space-y-5">
          <p>
            Based on <strong>SSAH</strong>, we posit that safety alignment only
            needs to teach the model the correct reasoning direction &mdash;
            either fulfilling or refusing &mdash; and equip it with a standard
            refusal mechanism. This leads to the insight that{' '}
            <strong>
              safety alignment can be achieved using only a small subset of
              critical computing units
            </strong>
            , since the task can be interpreted as a binary classification
            combined with a multi-selection task.
          </p>
        </div>

        <h3 className="subsection-title text-center">Identifying Safety-Critical Units</h3>
        <p className="text-center text-ink-500 max-w-2xl mx-auto -mt-2 mb-6">
          We categorize the computing units of LLMs into four groups:
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {units.map((u) => (
            <div key={u.abbr} className="card p-5 hover:shadow-card transition group">
              <div
                className={`inline-flex items-center justify-center h-10 w-10 rounded-lg ${u.color} text-white font-bold text-sm shadow-sm`}
              >
                {u.abbr}
              </div>
              <div className="mt-3 font-semibold text-ink-900 font-serif">{u.name}</div>
              <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">{u.desc}</p>
            </div>
          ))}
        </div>

        <div className="prose-paper max-w-3xl mx-auto mt-10 space-y-5">
          <p>
            To verify that different groups of computing units contribute
            exclusively, collectively, or not at all to safety and utility, we
            use a <strong>model pruning mechanism</strong>: removing components
            most closely linked to a specific attribute should significantly
            impact the model's performance in that area &mdash; a form of
            ablation. The most affected attributes reveal the critical
            components for that function.
          </p>
        </div>

        <Figure
          src={table1}
          alt="Table 1"
          caption="Table 1. Pruning-based ablation reveals attribute-critical components."
          widthClass="max-w-xl"
        />

        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Only <strong>1.3 – 1.4%</strong> of the model's units are
            exclusively responsible for safety attributes, confirming that
            safety alignment relies on a minimal subset of safety-critical
            components. Complex units play a supportive role; redundant units
            have no significant impact.
          </p>
        </div>

        <h3 className="subsection-title text-center">Why is Safety Brittle?</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Fine-tuning safety-aligned models for new tasks often compromises
            safety performance. During fine-tuning, safety-critical and complex
            units tend to be <em>repurposed</em> for utility tasks, weakening
            safety guardrails. This highlights the inherent brittleness of
            current safety alignment methods.
          </p>
        </div>

        <Figure
          src={figure5}
          alt="Figure 5"
          caption="Figure 5. Repurposing of safety-critical units during fine-tuning."
          widthClass="max-w-xl"
        />

        <h3 className="subsection-title text-center">Freezing Safety-Critical Components</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            To address brittleness, we propose <strong>freezing safety-critical components</strong>{' '}
            (ESUs and top-performing complex units) during fine-tuning.
            Experimental results demonstrate this approach significantly
            preserves safety performance while minimizing guardrail degradation.
          </p>
        </div>

        <Figure
          src={table2}
          alt="Table 2"
          caption="Table 2. Freezing safety-critical components preserves alignment under fine-tuning."
          widthClass="max-w-xl"
        />

        <h3 className="subsection-title text-center">Comparison with PEFT</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Our approach outperforms parameter-efficient fine-tuning baselines
            such as <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">LoRA</code>,{' '}
            <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">LLaMA-Adapter</code>,
            and <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">Prefix Tuning</code>,
            which degrade safety more severely. Preservation is not merely from
            freezing parameters &mdash; it results from{' '}
            <strong>accurately identifying and protecting safety-critical components</strong>.
          </p>
        </div>

        <Figure
          src={table3}
          alt="Table 3"
          caption="Table 3. Comparison against PEFT methods on safety and utility."
          widthClass="max-w-xl"
        />

        <h3 className="subsection-title text-center">
          Free Lunch: Redundant Units as Alignment Budget
        </h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            We extend these insights to explore whether redundant units (RUs)
            &mdash; accounting for at least <strong>20%</strong> of parameters
            in pre-trained LLMs &mdash; can be repurposed as a budget for
            safety alignment. Fine-tuning these redundant units alone can
            enhance safety alignment while reducing alignment tax.
          </p>
        </div>

        <Figure
          src={figure6}
          alt="Figure 6"
          caption="Figure 6. Redundant units as an alignment budget."
          widthClass="max-w-xl"
        />

        <h3 className="subsection-title text-center">Experimental Results</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Using pruning, we identified redundant units in LLaMA-7B and
            fine-tuned only these units for alignment. Alignment can be
            achieved with updates to just <strong>20% of the model's
            parameters</strong>, effectively eliminating alignment tax. This
            highlights the scalability and efficiency of our approach.
          </p>
        </div>

        <Figure
          src={table4}
          alt="Table 4"
          caption="Table 4. Alignment with the redundant-unit budget eliminates alignment tax."
          widthClass="max-w-xl"
        />
      </div>
    </section>
  );
}

export default LessIsMoreSection;
